import type { Move, Piece, Position } from '../../stores/gameStore'
import { generateMoves } from '../../ai/moveGen'
import {
  getPlayer1Home,
  getPlayer2Home,
  getZone3,
  getZone4,
  getZone5,
  getZone6,
} from '../../utils/board'
import { boardToXZ } from './constants'

// ── Board zones (players 1–6 = the six star points) ────────────────────────
const OPPOSITE: Record<number, number> = { 1: 2, 2: 1, 3: 6, 6: 3, 4: 5, 5: 4 }

// Classic marble colours, keyed by player/zone.
export const PLAYER_COLOR: Record<number, string> = {
  1: '#c0202c', // red    (bottom)
  2: '#1275ad', // blue   (top)
  3: '#e0a12a', // amber  (top-right)
  4: '#3f9d5a', // green  (bottom-right)
  5: '#8250b4', // purple (top-left)
  6: '#d9772e', // orange (bottom-left)
}

function parseCells(set: Set<string>): Position[] {
  return [...set].map((key) => {
    const [q, r] = key.split(',').map(Number)
    return { q, r }
  })
}

const ZONE_CELLS: Record<number, Position[]> = {
  1: parseCells(getPlayer1Home()),
  2: parseCells(getPlayer2Home()),
  3: parseCells(getZone3()),
  4: parseCells(getZone4()),
  5: parseCells(getZone5()),
  6: parseCells(getZone6()),
}

const keyOf = (p: Position) => `${p.q},${p.r}`

function hexDist(a: Position, b: Position): number {
  return (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(-a.q - a.r - (-b.q - b.r))) / 2
}

function seededRandom(seed: number) {
  let v = seed >>> 0 || 1
  return () => {
    v = (v * 1664525 + 1013904223) >>> 0
    return v / 0xffffffff
  }
}

// Which star points are in play, and their turn order. Each moves to its opposite.
function playersFor(count: 2 | 3 | 6): number[] {
  if (count === 6) return [1, 3, 5, 2, 4, 6]
  // Zones 2/3/5 sit 120° apart (a real triangle); each races to the empty
  // opposite corner {1,6,4}. Using adjacent corners would bunch them on one side.
  if (count === 3) return [2, 3, 5]
  return [1, 2]
}

// ── Game state ─────────────────────────────────────────────────────────────
interface LandingAnim {
  piece: Piece
  path: Position[]
  start: number
  duration: number
  x: number
  z: number
  hop: number
}

export interface LandingGame {
  players: number[]
  byPlayer: Map<number, Piece[]>
  all: Piece[]
  target: Record<number, number> // player → destination zone (flips when filled)
  prev: Map<Piece, string> // where each marble last came from (anti-backtrack)
  turnIdx: number
  anim: LandingAnim | null
  pauseUntil: number
  stall: number // consecutive turns without real forward progress → done/gridlocked
  rng: () => number
}

export function initLandingGame(count: 2 | 3 | 6, seed: number): LandingGame {
  const players = playersFor(count)
  const byPlayer = new Map<number, Piece[]>()
  const all: Piece[] = []
  const target: Record<number, number> = {}
  for (const p of players) {
    const pieces = ZONE_CELLS[p].map((c) => ({ position: { q: c.q, r: c.r }, player: p }))
    byPlayer.set(p, pieces)
    all.push(...pieces)
    target[p] = OPPOSITE[p]
  }
  return {
    players,
    byPlayer,
    all,
    target,
    prev: new Map(),
    turnIdx: 0,
    anim: null,
    pauseUntil: (seed % 5) * 0.3, // stagger the boards so they don't move in lockstep
    stall: 0,
    rng: seededRandom(seed),
  }
}

// Everyone's filled their destination (or is deadlocked): flip every player's
// target so the marbles turn around and race back — no reset, nothing vanishes.
function reverse(game: LandingGame) {
  for (const p of game.players) game.target[p] = game.target[p] === p ? OPPOSITE[p] : p
  game.prev.clear()
  game.turnIdx = 0
  game.stall = 0
}

interface Chosen {
  move: Move
  piece: Piece
  forward: boolean
}

// Fast fill-distance move: send each marble to the nearest still-empty hole of
// its destination home, preferring long jumps. Marbles never retreat, but when a
// marble can't advance it may step sideways (equal distance) to route around a
// jam — so busy boards keep flowing instead of piling up. Returns null when this
// player's home is full, or it has no non-retreating move.
function chooseMove(game: LandingGame, player: number): Chosen | null {
  const homeCells = ZONE_CELLS[game.target[player]]
  const mine = game.byPlayer.get(player)!
  const byKey = new Map(mine.map((m) => [keyOf(m.position), m] as const))
  const homeKeys = new Set(homeCells.map(keyOf))
  const openHome = homeCells.filter((c) => !byKey.has(keyOf(c)))
  if (openHome.length === 0) return null // home is full → done

  const distTo = (pos: Position) => {
    if (homeKeys.has(keyOf(pos))) return 0 // already settled in a home hole
    let d = Infinity
    for (const c of openHome) {
      const h = hexDist(pos, c)
      if (h < d) d = h
    }
    return d
  }

  const moves = generateMoves(game.all, player)
  let best: Move | null = null
  let bestPiece: Piece | null = null
  let bestScore = -Infinity
  let bestForward = false
  for (const m of moves) {
    const progress = distTo(m.start) - distTo(m.end)
    if (progress < 0) continue // never retreat
    const piece = byKey.get(keyOf(m.start))
    if (!piece) continue
    // Don't step straight back to where this marble just came from.
    if (progress === 0 && game.prev.get(piece) === keyOf(m.end)) continue
    const score = progress * 10 + m.path.length * 0.6 + game.rng() * 0.5
    if (score > bestScore) {
      bestScore = score
      best = m
      bestPiece = piece
      bestForward = progress > 0
    }
  }
  return best && bestPiece ? { move: best, piece: bestPiece, forward: bestForward } : null
}

const SEG_TIME = 0.34 // seconds per hop/segment
const PAUSE = 0.12 // between moves
const HOP = 0.7 // arc height
const easeInOut = (t: number) => 0.5 - 0.5 * Math.cos(Math.PI * t)

export function advanceLandingGame(game: LandingGame, t: number) {
  let a = game.anim
  if (!a) {
    if (t < game.pauseUntil) return

    // Whole board filled or gridlocked (a couple of rounds with no forward
    // progress): flip everyone and play back the other way.
    if (game.stall >= game.players.length * 6) {
      reverse(game)
      return
    }

    const player = game.players[game.turnIdx]
    const chosen = chooseMove(game, player)
    if (!chosen) {
      // This player is done (or fully boxed in): pass to the next.
      game.turnIdx = (game.turnIdx + 1) % game.players.length
      game.stall += 1
      return
    }
    game.stall = chosen.forward ? 0 : game.stall + 1
    a = {
      piece: chosen.piece,
      path: chosen.move.path,
      start: t,
      duration: (chosen.move.path.length - 1) * SEG_TIME + 0.02,
      x: 0,
      z: 0,
      hop: 0,
    }
    game.anim = a
    // Fall through so the marble is positioned on this very frame (p ≈ 0 → its
    // start hole) instead of flashing to the board origin for one frame.
  }

  const segs = a.path.length - 1
  const p = Math.min(1, (t - a.start) / a.duration)
  const fp = p * segs
  const seg = Math.min(segs - 1, Math.floor(fp))
  const local = fp - seg
  const from = boardToXZ(a.path[seg])
  const to = boardToXZ(a.path[seg + 1])
  const e = easeInOut(local)
  a.x = from.x + (to.x - from.x) * e
  a.z = from.z + (to.z - from.z) * e
  a.hop = Math.sin(local * Math.PI) * HOP
  if (p >= 1) {
    const last = a.path[a.path.length - 1]
    game.prev.set(a.piece, keyOf(a.path[0]))
    a.piece.position.q = last.q
    a.piece.position.r = last.r
    game.anim = null
    game.pauseUntil = t + PAUSE
    game.turnIdx = (game.turnIdx + 1) % game.players.length
  }
}
